// src/components/main/LoadingOverlay.jsx
import React, { useContext, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LoadingContext } from '../../contexts/LoadingContext';

// Брендовые буквы
const BRAND = ['S', 'E', 'S'];

/* ——— Сфера «чёрной дыры» ——— */
const BlackHole = ({ size = 140 }) => (
  <div className="relative" style={{ width: size, height: size }}>
    {/* мягкое свечение */}
    <div className="absolute inset-[-24%] rounded-full blur-2xl bg-violet-600/25" />
    {/* аккреционный диск */}
    <motion.div
      className="absolute inset-[-18%] rounded-full"
      style={{
        background:
          'conic-gradient(from 0deg, rgba(168,85,247,0.00) 0%, rgba(168,85,247,0.35) 20%, rgba(236,72,153,0.25) 40%, rgba(168,85,247,0.35) 60%, rgba(168,85,247,0.00) 100%)',
        filter: 'blur(10px)',
      }}
      animate={{ rotate: 360 }}
      transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
    />
    {/* горизонт событий */}
    <div className="absolute inset-0 rounded-full bg-black" />
    {/* стеклянный блик */}
    <div className="absolute inset-2 rounded-full bg-gradient-to-b from-white/5 to-black/20" />
    {/* тонкий ореол */}
    <div className="absolute inset-0 rounded-full ring-2 ring-violet-500/25" />
  </div>
);

/* ——— Одна буква, которую «затягивает» в центр ——— */
const SuckedLetter = ({
  ch,
  angle,             // направление полёта (в градусах)
  startRadius = 150, // откуда стартует
  duration = 2.2,
  delay = 0,
}) => {
  const rad = (angle * Math.PI) / 180;
  const x0 = Math.cos(rad) * startRadius;
  const y0 = Math.sin(rad) * startRadius;

  return (
    <motion.span
      className="absolute left-1/2 top-1/2 text-3xl font-extrabold select-none"
      style={{ textShadow: '0 0 12px rgba(139,92,246,.7)' }}
      initial={{ x: x0, y: y0, opacity: 0, scale: 1, rotate: 0 }}   // ← ВАЖНО
      animate={{
        x: [x0, x0 * 0.5, 0],
        y: [y0, y0 * 0.5, 0],
        opacity: [0, 1, 0],
        scale: [1, 1, 0.2],
      }}
      transition={{
        duration,
        times: [0, 0.65, 1],
        delay,
        repeat: Infinity,
        repeatDelay: 0.25,
        ease: 'easeInOut',
      }}
    >
      <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-400 via-fuchsia-400 to-violet-400">
        {ch}
      </span>
    </motion.span>
  );
};

const LoadingOverlay = () => {
  const { isLoading, loadingMessage } = useContext(LoadingContext);

  // Поток букв по окружности
  const letters = useMemo(() => {
    const COUNT = 18;
    return Array.from({ length: COUNT }, (_, i) => ({
      ch: BRAND[i % BRAND.length],
      angle: (i * 360) / COUNT,
      delay: i * 0.12,
    }));
  }, []);

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          className="fixed inset-0 z-50 grid place-items-center 
                     bg-black/70 backdrop-blur-xl 
                     [background-image:radial-gradient(1000px_500px_at_50%_-10%,rgba(139,92,246,0.25),transparent)]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* тонкая сетка */}
          <div className="pointer-events-none absolute inset-0 opacity-[0.07] [mask-image:radial-gradient(circle_at_center,black,transparent_68%)]">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="32" height="32" patternUnits="userSpaceOnUse">
                  <path d="M 32 0 L 0 0 0 32" fill="none" stroke="white" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>

          {/* центр */}
          <div className="relative flex flex-col items-center gap-6 text-center" role="status" aria-live="polite">
            <div className="relative w-[360px] h-[260px]">
              {/* ЛЕТЯЩИЕ БУКВЫ (под сферой) */}
              <div className="absolute inset-0 z-0">
                {letters.map((l, i) => (
                  <SuckedLetter key={i} ch={l.ch} angle={l.angle} delay={l.delay} />
                ))}
              </div>

              {/* СФЕРА В ЦЕНТРЕ (поверх букв, чтобы «поглощала» их) */}
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                <BlackHole size={150} />
              </div>
            </div>

            {/* подписи и прогресс */}
            <div className="space-y-3">
              <div className="text-sm font-medium text-white/90">Загрузка</div>
              <div className="mx-auto h-1.5 w-[300px] overflow-hidden rounded-full bg-white/10">
                <motion.div
                  className="h-full w-1/3 bg-gradient-to-r from-transparent via-violet-400 to-transparent"
                  animate={{ x: ['-33%', '133%'] }}
                  transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                />
              </div>
              {loadingMessage && (
                <motion.div
                  className="text-sm text-white/80"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35, delay: 0.1 }}
                >
                  {loadingMessage}
                </motion.div>
              )}
            </div>
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent" />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingOverlay;
